"use client";

import { motion } from "framer-motion";
import Image from "next/image";

export default function HeroSection() {
    return (
        <section
            id="hero"
            className="section-snap relative flex items-center justify-center bg-background min-h-screen overflow-hidden"
        >
            {/* Background subtle pattern */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-30" />

            <div className="container mx-auto px-section-x py-section-y relative z-10">
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-0">
                    {/* Left Content */}
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1], delay: 0.4 }}
                        className="flex-1 w-full lg:w-1/2 flex flex-col justify-center"
                    >
                        {/* Logo */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="mb-8"
                        >
                            <h2 className="text-3xl font-bold tracking-tight text-foreground" style={{ fontFamily: 'var(--font-playfair), Georgia, serif' }}>
                                HSN
                                <span className="text-accent">.</span>
                            </h2>
                        </motion.div>

                        {/* Headline */}
                        <h1 className="text-hero font-bold text-foreground leading-tight mb-6">
                            Welcome
                            <br />
                            I&apos;m Hasan
                            <span className="text-accent">.</span>
                        </h1>

                        {/* Role Descriptor */}
                        <p className="text-body text-text-secondary leading-relaxed mb-8 max-w-lg">
                            I&apos;m a{" "}
                            <span className="font-semibold text-foreground">
                                Flutter Developer & UI/UX Engineer
                            </span>{" "}
                            who builds beautiful, functional mobile experiences with
                            intentional design thinking.
                        </p>

                        {/* CTA Buttons */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.8 }}
                            className="flex flex-wrap gap-4"
                        >
                            <motion.a
                                whileHover={{ scale: 1.05, y: -2 }}
                                whileTap={{ scale: 0.95 }}
                                href="#projects"
                                className="bg-accent hover:bg-accent-hover text-white px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg shadow-accent/20"
                            >
                                View My Work
                            </motion.a>
                            <motion.a
                                whileHover={{ scale: 1.05, y: -2 }}
                                whileTap={{ scale: 0.95 }}
                                href="#contact"
                                className="border-2 border-foreground text-foreground hover:bg-foreground hover:text-background px-8 py-4 rounded-lg font-semibold transition-colors"
                            >
                                Get in Touch
                            </motion.a>
                        </motion.div>
                    </motion.div>

                    {/* Right Content - Avatar */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1], delay: 0.6 }}
                        className="flex-1 w-full lg:w-1/2 flex justify-center items-center"
                    >
                        <div className="relative">
                            {/* Background accent shape */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.6, delay: 0.8 }}
                                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-24 bg-accent/10 -z-10"
                            />

                            {/* Avatar Image */}
                            <div className="relative w-72 h-72 md:w-96 md:h-96">
                                <Image
                                    src="/avatar.png"
                                    alt="Hasan - Flutter Developer & UI/UX Engineer"
                                    fill
                                    className="object-contain"
                                    priority
                                    fetchPriority="high"
                                    loading="eager"
                                    sizes="(max-width: 768px) 288px, 384px"
                                />
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Scroll Indicator Text */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6, delay: 1.2 }}
                    className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
                >
                    <span className="text-xs text-text-secondary/60 tracking-widest uppercase">
                        Scroll
                    </span>
                    <motion.div
                        animate={{ y: [0, 8, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                        className="w-px h-8 bg-border"
                    />
                </motion.div>
            </div>
        </section>
    );
}
